import { http, eventSink, session, constants } from 'lib_ui-services';
import { sendToFile } from '../../../CRUD/namespace/relation/exportHelpers/exportHelper';

const pending = new Set();
export const _private = {
    http,
    eventSink,
    onSessionChange: session.onSessionChange,
    sendToFile,
    wait: ms => new Promise(resolve => setTimeout(resolve, ms))
};

export default {
    verb: 'doingMark',
    namespace: 'deviceManagement',
    relation: 'device',
    description: 'Request a named reader service action from any reader toolbar',
    prerequisites: [],
    logic: serviceReader
};

async function serviceReader({ data, context, dispatch }) {
    if (!context.type?.startsWith('reader.')) return;
    const record = data.activeRecord?.record || data.record;
    if (!record?._id || data.activeRecord?.isNew) return;
    const actionId = context.type.slice('reader.'.length);
    const group = context.session.groupNumber || 0;
    const scope = `${group}:${context.session.tenant?.['identity:tenant']?._id}:${record._id}`;
    if (pending.has(scope)) return;
    pending.add(scope);
    const base = `/g/${group}/api/deviceManagement/device/${record._id}/diagnostics`;
    // Before acceptance, changing the selection or session cancels the request.
    // Once accepted, the server owns the action and finishes it even if the tab closes.
    let active = true;
    let resolveConfirmation;
    const [subscribe] = _private.eventSink(context.eventBoundaryContext);
    const cancel = () => {
        active = false;
        // Navigation can remove the dialog without calling either button callback.
        resolveConfirmation?.(false);
    };
    const unsubscribers = [
        subscribe({ verb: 'edit', namespace: 'deviceManagement', relation: 'device' }, cancel),
        subscribe({ verb: 'cancel', namespace: 'deviceManagement', relation: 'device' }, cancel),
        _private.onSessionChange(cancel)
    ];
    try {
        if (actionId === 'bundle') {
            const bundle = await _private.http.get(`${base}/bundle`);
            if (active)
                _private.sendToFile(
                    `${record.title}-support.json`,
                    new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
                );
            return;
        }
        if (actionId === 'firmware' || actionId === 'verifyFirmware') {
            const firmwareUrl = base.replace(/diagnostics$/, 'firmware');
            const available = await _private.http.get(firmwareUrl);
            if (!active) return;
            if (actionId === 'verifyFirmware') {
                if (available.operation?.status !== 'needs-attention')
                    throw new Error('No firmware update is waiting for verification.');
                const operation = await _private.http.post(`${firmwareUrl}/verify`, {});
                return { ...data, operation };
            }
            if (['active', 'needs-attention'].includes(available.operation?.status))
                throw new Error('A firmware update is already in progress. Check its status before requesting another.');
            if (!available.releases?.length)
                throw new Error('No firmware releases are available for this reader.');
            const selected = await new Promise(resolve => {
                resolveConfirmation = resolve;
                dispatch({
                    title: `Update firmware on ${record.title}`,
                    message: [
                        'The reader will stop reading and restart. Keep its power and network connected until verification finishes.',
                        'Backbone checks compatibility before updating, then restores the app and reader settings.'
                    ],
                    choiceLabel: 'Firmware release', choices: available.releases,
                    okButtonText: 'Update firmware', cancelButtonText: 'Cancel',
                    okAction: resolve, cancelAction: () => resolve(null)
                }, { verb: 'confirm', namespace: 'application', relation: 'user' });
            });
            resolveConfirmation = undefined;
            if (!selected || !active) return;
            const operation = await _private.http.post(firmwareUrl, { releaseId: selected._id, confirmed: true });
            if (active) await dispatch(
                { message: 'Firmware update requested. Check Health for the result.', type: constants.notificationTypes.TOAST },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
            return { ...data, operation };
        }
        if (actionId === 'events') {
            // Read every saved page, even when the Reader Events tab is closed.
            // Live tag messages are not saved and are not part of this download.
            const messages = [];
            let cursor;
            do {
                const page = await _private.http.get(`${base}/events${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
                if (!active) return;
                messages.push(...page.messages);
                cursor = page.nextCursor;
            } while (cursor);
            _private.sendToFile(
                `${record.title}-communication-history.json`,
                new Blob([JSON.stringify(messages, null, 2)], { type: 'application/json' })
            );
            return;
        }
        const available = await _private.http.get(`${base}/actions`);
        if (!active) return;
        const action = available.availableActions.find(item => item.id === actionId);
        if (!action) throw new Error('This reader action is unavailable.');
        if (['active', 'needs-attention'].includes(available.operation?.status))
            throw new Error('A reader action is already in progress. Wait for it to finish.');
        if (action.service) {
            const confirmed = await new Promise(resolve => {
                resolveConfirmation = resolve;
                dispatch(
                    {
                        title: action.title,
                        message: action.effect,
                        okButtonText: 'Confirm',
                        cancelButtonText: 'Cancel',
                        okAction: () => resolve(true),
                        cancelAction: () => resolve(false)
                    },
                    { verb: 'confirm', namespace: 'application', relation: 'user' }
                );
            });
            resolveConfirmation = undefined;
            if (!confirmed || !active) return;
        }
        const operation = await _private.http.post(`${base}/actions`, { action: actionId, confirmed: action.service });
        if (actionId === 'systemLogs') {
            if (!active) return;
            await dispatch(
                {
                    message: 'Collecting Zebra system logs… The download will start when they are ready.',
                    type: constants.notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
            const download = `${base}/system-logs/${operation.operationId}`;
            // Poll only this explicit download request, never the reader itself.
            // A later health check must not redirect us to a different operation.
            for (let attempt = 0; attempt < 30 && active; attempt++) {
                const result = await _private.http.get(download);
                if (!active) return;
                if (result.status === 'succeeded') {
                    const content = await _private.http.get(`${download}/content`);
                    if (active) _private.sendToFile(`${record.title}-syslog.tar.gz`, content);
                    return;
                }
                if (result.status !== 'active')
                    throw new Error(result.reason || 'Unable to collect Zebra system logs. Try again.');
                await _private.wait(2000);
            }
            if (active) throw new Error('System logs are taking longer than expected. Try again shortly.');
            return;
        }
        if (active)
            await dispatch(
                { message: `${action.title} requested.`, type: constants.notificationTypes.TOAST },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        return { ...data, operation };
    } catch (error) {
        if (active)
            await dispatch(
                {
                    message: error.message || 'Unable to request reader action.',
                    variant: 'error',
                    isError: true,
                    type: constants.notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        throw error;
    } finally {
        unsubscribers.forEach(unsubscribe => unsubscribe?.());
        pending.delete(scope);
    }
}
